import React, { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Box,
  Flex,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Input,
  Button,
  Tooltip,
} from "@chakra-ui/react";
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatUnderlined,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatQuote,
  MdLink,
  MdLinkOff,
  MdImage,
} from "react-icons/md";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";

// This toolbar is intentionally a 1:1 match with the backend's sanitize-html
// allow-list (Validators/shared/security.js: p, b/strong, i/em, u, a, ul, ol,
// li, br, blockquote, h2, h3, h4, img). Any tag not offered here would be
// silently stripped on save, so don't add buttons for marks/nodes beyond
// this list without updating the backend allow-list first.

const HEADING_LEVELS = [2, 3, 4];

function ToolbarButton({ label, icon, isActive, onClick, isDisabled }) {
  return (
    <Tooltip label={label} fontSize="xs">
      <IconButton
        type="button"
        aria-label={label}
        icon={icon}
        size="sm"
        variant={isActive ? "solid" : "ghost"}
        bg={isActive ? GOLD_BORDER : "transparent"}
        color={isActive ? "#1A1A1A" : "gray.600"}
        _hover={{ bg: isActive ? GOLD_BORDER : GOLD_LIGHT }}
        onClick={onClick}
        isDisabled={isDisabled}
      />
    </Tooltip>
  );
}

function LinkPopover({ editor }) {
  const [url, setUrl] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const openPopover = () => {
    setUrl(editor.getAttributes("link").href || "");
    setIsOpen(true);
  };

  const applyLink = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: trimmed }).run();
    }
    setIsOpen(false);
  };

  return (
    <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement="bottom-start">
      <PopoverTrigger>
        <span>
          <ToolbarButton
            label="Link"
            icon={<MdLink />}
            isActive={editor.isActive("link")}
            onClick={openPopover}
            isDisabled={editor.state.selection.empty}
          />
        </span>
      </PopoverTrigger>
      <PopoverContent w="260px">
        <PopoverArrow />
        <PopoverBody>
          <Flex gap={2}>
            <Input
              size="sm"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } }}
            />
            <Button type="button" size="sm" bg={GOLD_BORDER} color="#1A1A1A" _hover={{ bg: GOLD_TEXT, color: "white" }} onClick={applyLink}>
              Set
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

function ImagePopover({ editor }) {
  const [url, setUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const insertImage = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setIsOpen(false);
      return;
    }
    editor.chain().focus().setImage({ src: trimmed, alt: alt.trim() || undefined }).run();
    setUrl("");
    setAlt("");
    setIsOpen(false);
  };

  return (
    <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement="bottom-start">
      <PopoverTrigger>
        <span>
          <ToolbarButton label="Image (by URL)" icon={<MdImage />} onClick={() => setIsOpen(true)} />
        </span>
      </PopoverTrigger>
      <PopoverContent w="280px">
        <PopoverArrow />
        <PopoverBody>
          <Flex direction="column" gap={2}>
            <Input
              size="sm"
              placeholder="Image URL (https://...)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <Input
              size="sm"
              placeholder="Alt text (optional)"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); insertImage(); } }}
            />
            <Button type="button" size="sm" bg={GOLD_BORDER} color="#1A1A1A" _hover={{ bg: GOLD_TEXT, color: "white" }} onClick={insertImage}>
              Insert
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

function Toolbar({ editor }) {
  if (!editor) return null;

  return (
    <Flex wrap="wrap" gap="2px" borderBottomWidth="1px" borderColor={GOLD_BORDER} bg="gray.50" p="4px" borderTopRadius="md">
      <ToolbarButton
        label="Bold"
        icon={<MdFormatBold />}
        isActive={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        label="Italic"
        icon={<MdFormatItalic />}
        isActive={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        label="Underline"
        icon={<MdFormatUnderlined />}
        isActive={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <LinkPopover editor={editor} />
      <ToolbarButton
        label="Remove link"
        icon={<MdLinkOff />}
        onClick={() => editor.chain().focus().unsetLink().run()}
        isDisabled={!editor.isActive("link")}
      />

      <Box w="1px" bg="gray.200" mx="4px" />

      {HEADING_LEVELS.map((level) => (
        <ToolbarButton
          key={level}
          label={`Heading ${level}`}
          icon={<Box as="span" fontSize="xs" fontWeight="700">H{level}</Box>}
          isActive={editor.isActive("heading", { level })}
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
        />
      ))}
      <ToolbarButton
        label="Paragraph"
        icon={<Box as="span" fontSize="xs" fontWeight="700">P</Box>}
        isActive={editor.isActive("paragraph")}
        onClick={() => editor.chain().focus().setParagraph().run()}
      />

      <Box w="1px" bg="gray.200" mx="4px" />

      <ToolbarButton
        label="Bullet list"
        icon={<MdFormatListBulleted />}
        isActive={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        label="Numbered list"
        icon={<MdFormatListNumbered />}
        isActive={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolbarButton
        label="Quote"
        icon={<MdFormatQuote />}
        isActive={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />

      <Box w="1px" bg="gray.200" mx="4px" />

      <ImagePopover editor={editor} />
    </Flex>
  );
}

// Controlled rich-text editor whose output HTML is restricted, by
// construction, to the backend's sanitizeHtml allow-list — every extension
// enabled here maps to a tag the server keeps, so nothing typed here is
// silently dropped after save.
function RichTextEditor({ value, onChange, placeholder, dir = "ltr", isInvalid }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: HEADING_LEVELS },
        strike: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        protocols: ["http", "https", "mailto"],
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
      Image.extend({
        addAttributes() {
          return {
            src: { default: null },
            alt: { default: null },
          };
        },
      }),
      Placeholder.configure({ placeholder: placeholder || "" }),
    ],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  // Keep the editor in sync when the parent resets/loads content (e.g.
  // switching locale tabs or opening the edit modal for a different item).
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming !== current && incoming !== "<p></p>") {
      editor.commands.setContent(incoming, false);
    } else if (!incoming && current !== "<p></p>") {
      editor.commands.setContent("", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  return (
    <Box
      borderWidth="1px"
      borderColor={isInvalid ? "red.500" : GOLD_BORDER}
      borderRadius="md"
      bg="white"
      dir={dir}
    >
      <Toolbar editor={editor} />
      <Box
        px="12px"
        py="8px"
        minH="160px"
        maxH="360px"
        overflowY="auto"
        sx={{
          ".ProseMirror": { outline: "none" },
          ".ProseMirror p": { margin: "0 0 8px" },
          ".ProseMirror h2": { fontSize: "1.3em", fontWeight: "700", margin: "12px 0 6px" },
          ".ProseMirror h3": { fontSize: "1.15em", fontWeight: "700", margin: "10px 0 6px" },
          ".ProseMirror h4": { fontSize: "1.05em", fontWeight: "700", margin: "8px 0 6px" },
          ".ProseMirror ul, .ProseMirror ol": { paddingInlineStart: "24px", margin: "0 0 8px" },
          ".ProseMirror blockquote": {
            borderInlineStart: `3px solid ${GOLD_BORDER}`,
            paddingInlineStart: "12px",
            color: "gray.600",
            margin: "0 0 8px",
          },
          ".ProseMirror img": { maxWidth: "100%", borderRadius: "4px" },
          ".ProseMirror a": { color: GOLD_TEXT, textDecoration: "underline" },
          ".ProseMirror p.is-editor-empty:first-of-type::before": {
            content: "attr(data-placeholder)",
            color: "gray.400",
            float: dir === "rtl" ? "right" : "left",
            height: 0,
            pointerEvents: "none",
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}

export default RichTextEditor;
